var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { Button, IconButton, Layout, MultiColumnList, } from '@folio/stripes/components';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocaleWeekdays } from '../../utils/WeekdayUtils';
import css from './HoursAndExceptionFields.css';
import { calculateInitialRows, getConflictError, getTimeError, getWeekdayError, isRowConflicted, } from './HoursOfOperationFieldUtils';
import MCLRowFormatter from './MCLRowFormatter';
import OpenClosedSelect from './OpenClosedSelect';
import RowType from './RowType';
import TimeField from './TimeField';
import WeekdayPicker from './WeekdayPicker';
function updateRowState(rowStates, setRowStates, rowIndex, newState) {
    var newRowState = __spreadArray([], rowStates, true);
    newRowState[rowIndex] = __assign(__assign({}, newRowState[rowIndex]), newState);
    setRowStates(newRowState);
}
export var HoursOfOperationField = function (props) {
    var intl = useIntl();
    var localeWeekdays = useLocaleWeekdays(intl);
    /** Must add at least one empty row, or MCL will not render properly */
    var _a = useState([
        {
            i: -1,
            type: RowType.Open,
            startDay: undefined,
            startTime: undefined,
            endDay: undefined,
            endTime: undefined,
        },
    ]), rowStates = _a[0], _setRowStates = _a[1];
    var setRowStates = function (newRowStates) {
        _setRowStates(newRowStates);
        props.input.onChange(newRowStates);
    };
    // destructured separately as one is re-assignable
    var _b = useState(0), currentCount = _b[0], setCurrentCount = _b[1];
    // Initially sort and use values as source of rows
    useEffect(function () {
        // do nothing if there are no openings to render/parse
        if (props.isNewCalendar) {
            return;
        }
        var _a = calculateInitialRows(props.input.value, localeWeekdays), rows = _a.rows, count = _a.count;
        setRowStates(rows);
        setCurrentCount(count);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [localeWeekdays]);
    var contents = rowStates.map(function (row, realIndex) {
        return {
            rowState: row,
            status: (React.createElement(OpenClosedSelect, { value: row.type, onBlur: props.input.onBlur, onChange: function (newData) {
                    if (newData.type === RowType.Closed) {
                        newData.startTime = undefined;
                        newData.endTime = undefined;
                    }
                    updateRowState(rowStates, setRowStates, realIndex, newData);
                } })),
            startDay: (React.createElement(WeekdayPicker, { ariaLabel: intl.formatMessage({
                    id: 'ui-calendar.calendarForm.openings.column.startDay',
                }), value: row.startDay, onChange: function (newWeekday) {
                    updateRowState(rowStates, setRowStates, realIndex, { startDay: newWeekday });
                    props.input.onBlur();
                }, error: getWeekdayError('startDay', props.error, row.i, props.submitAttempted, props.meta.touched) })),
            startTime: (React.createElement(TimeField, { display: row.type === RowType.Open, value: row.startTime, inputRef: function (el) {
                    props.timeFieldRefs.startTime[row.i] = el;
                }, error: getTimeError('startTime', props.error, row.i, props.submitAttempted, props.meta.touched), onBlur: props.input.onBlur, onChange: function (newValue) { return updateRowState(rowStates, setRowStates, realIndex, { startTime: newValue }); } })),
            endDay: (React.createElement(WeekdayPicker, { ariaLabel: intl.formatMessage({
                    id: 'ui-calendar.calendarForm.openings.column.endDay',
                }), value: row.endDay, onChange: function (newWeekday) {
                    updateRowState(rowStates, setRowStates, realIndex, { endDay: newWeekday });
                    props.input.onBlur();
                }, error: getWeekdayError('endDay', props.error, row.i, props.submitAttempted, props.meta.touched) })),
            endTime: (React.createElement(TimeField, { display: row.type === RowType.Open, value: row.endTime, inputRef: function (el) {
                    props.timeFieldRefs.endTime[row.i] = el;
                }, error: getTimeError('endTime', props.error, row.i, props.submitAttempted, props.meta.touched), onBlur: props.input.onBlur, onChange: function (newValue) { return updateRowState(rowStates, setRowStates, realIndex, { endTime: newValue }); } })),
            actions: (React.createElement(Layout, { className: "full flex centerContent" },
                React.createElement(IconButton, { icon: "trash", onClick: function () {
                        var newRowStates = __spreadArray([], rowStates, true);
                        newRowStates.splice(realIndex, 1);
                        delete props.timeFieldRefs.startTime[row.i];
                        delete props.timeFieldRefs.endTime[row.i];
                        setRowStates(newRowStates);
                        props.input.onBlur();
                    } }))),
            isConflicted: isRowConflicted(props.error, row.i),
        };
    });
    contents.push({
        rowState: {
            i: -1,
            type: RowType.Open,
            startDay: undefined,
            startTime: undefined,
            endDay: undefined,
            endTime: undefined,
        },
        status: (React.createElement(Button, { marginBottom0: true, onClick: function () {
                var newRowStates = __spreadArray([], rowStates, true);
                newRowStates.push({
                    i: currentCount,
                    type: RowType.Open,
                    startDay: undefined,
                    startTime: undefined,
                    endDay: undefined,
                    endTime: undefined,
                });
                setCurrentCount(currentCount + 1);
                setRowStates(newRowStates);
            } },
            React.createElement(FormattedMessage, { id: "ui-calendar.calendarForm.addRowButton" }))),
        startDay: undefined,
        startTime: undefined,
        endDay: undefined,
        endTime: undefined,
        actions: undefined,
        isConflicted: false,
    });
    var conflictError = getConflictError(props.error);
    return (React.createElement(React.Fragment, null,
        React.createElement(MultiColumnList, { interactive: false, rowMetadata: ['isConflicted', 'rowState'], columnIdPrefix: "hours-of-operation", columnMapping: {
                status: React.createElement(FormattedMessage, { id: "ui-calendar.calendarForm.openings.column.status" }),
                startDay: React.createElement(FormattedMessage, { id: "ui-calendar.calendarForm.openings.column.startDay" }),
                startTime: React.createElement(FormattedMessage, { id: "ui-calendar.calendarForm.openings.column.startTime" }),
                endDay: React.createElement(FormattedMessage, { id: "ui-calendar.calendarForm.openings.column.endDay" }),
                endTime: React.createElement(FormattedMessage, { id: "ui-calendar.calendarForm.openings.column.endTime" }),
                actions: React.createElement(FormattedMessage, { id: "ui-calendar.calendarForm.openings.column.actions" }),
            }, columnWidths: {
                status: '14%',
                startDay: '20%',
                startTime: '20%',
                endDay: '20%',
                endTime: '20%',
                actions: '6%',
            }, contentData: contents, getCellClass: function (defaultClasses, rowData) {
                var _a;
                return classNames(defaultClasses, css.cellWrapper, (_a = {},
                    _a[css.conflictCell] = rowData.isConflicted,
                    _a));
            }, rowFormatter: (MCLRowFormatter) }),
        conflictError));
};
export default HoursOfOperationField;
